/** Small shared pieces of the WS7 pages. */
import type { UseQueryResult } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { ApiError } from "../../api/client";
import { useAnnounce } from "../../components/announce";
import { ErrorBox } from "../../components/ErrorBox";
import { copyText } from "../../lib/md";

/** 409 → what changed + Reload; anything else → ErrorBox (422 lists every detail). */
export function ActionError({ error, onReload }: { error: unknown; onReload?: () => void }) {
  if (!error) return null;
  if (error instanceof ApiError && error.status === 409) {
    const cur = error.body.current;
    const version = cur && typeof cur === "object" && "version" in cur ? String((cur as { version: unknown }).version) : null;
    return (
      <div role="alert" className="error-box" data-testid="conflict">
        <strong>Conflict (409): {error.message}</strong>
        <p>
          Someone else changed this first{version ? ` (now at version ${version})` : ""}. Reload to see their change,
          then decide again.
        </p>
        {onReload && (
          <button type="button" data-testid="conflict-reload" onClick={onReload}>
            Reload
          </button>
        )}
      </div>
    );
  }
  return <ErrorBox error={error} />;
}

/** Loading / error / data for one query. */
export function QueryView<T>({ q, what, children }: { q: UseQueryResult<T>; what: string; children: (data: T) => ReactNode }) {
  if (q.isPending) return <p className="ws7-muted" data-testid="loading">Loading {what}…</p>;
  if (q.isError) return <ErrorBox error={q.error} />;
  return <>{children(q.data)}</>;
}

export function Section({ id, title, actions, children }: { id: string; title: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="ws7-section" aria-labelledby={`${id}-h`} data-testid={`section-${id}`}>
      <div className="ws7-section-head">
        <h2 id={`${id}-h`}>{title}</h2>
        {actions && <div className="ws7-section-actions">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

/** Scrollable table wrapper (keyboard-focusable so the overflow is reachable without a mouse). */
export function TableScroll({ label, children, className = "ws7-scroll" }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={className} role="region" aria-label={label} tabIndex={0}>
      {children}
    </div>
  );
}

/** Copy MD / Download MD for pages the server has no exporter for: Markdown is built from the data on screen.
 * Same test ids and names as the spine's MdButtons. */
export function ClientMdButtons({ filename, markdown }: { filename: string; markdown: string | null }) {
  const announce = useAnnounce();
  const href = markdown == null ? undefined : `data:text/markdown;charset=utf-8,${encodeURIComponent(markdown)}`;
  return (
    <span className="md-buttons">
      <button
        type="button"
        data-testid="copy-md"
        aria-label="Copy Markdown"
        disabled={markdown == null}
        onClick={async () => {
          try {
            await copyText(markdown ?? "");
            announce("Markdown copied");
          } catch (e) {
            announce(`Copy failed: ${(e as Error).message}`, "error");
          }
        }}
      >
        Copy MD
      </button>
      <a className="button" data-testid="download-md" aria-label="Download Markdown" href={href} download={filename}>
        Download MD
      </a>
    </span>
  );
}

/** A destructive action needs a second, explicit click (no window.confirm: bots cannot answer dialogs). */
export function ConfirmButton({ label, confirmLabel, testId, onConfirm, disabled, className = "ws7-danger", ariaLabel }: {
  label: string;
  ariaLabel?: string;
  confirmLabel: string;
  testId: string;
  onConfirm: () => void;
  disabled?: boolean;
  className?: string;
}) {
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return (
      <button type="button" className={className} data-testid={testId} aria-label={ariaLabel} disabled={disabled} onClick={() => setAsking(true)}>
        {label}
      </button>
    );
  }
  return (
    <span className="ws7-inline">
      <button
        type="button"
        className={className}
        data-testid={`${testId}-confirm`}
        onClick={() => {
          setAsking(false);
          onConfirm();
        }}
      >
        {confirmLabel}
      </button>
      <button type="button" data-testid={`${testId}-cancel`} onClick={() => setAsking(false)}>
        Keep
      </button>
    </span>
  );
}

/** Copy a string (token, login link, route) and announce it. */
export function CopyButton({ text, label, testId, what, ariaLabel }: {
  text: string;
  label: string;
  testId: string;
  what: string;
  ariaLabel?: string;
}) {
  const announce = useAnnounce();
  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={ariaLabel}
      onClick={async () => {
        try {
          await copyText(text);
          announce(`Copied ${what}`);
        } catch (e) {
          announce(`Copy failed: ${(e as Error).message}`, "error");
        }
      }}
    >
      {label}
    </button>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="ws7-muted" data-testid="empty">{children}</p>;
}
